import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import Disclosure from 'modules/payroll_bundle/components/Disclosure'
import { useFetchPaymentData } from 'modules/payroll_bundle/hooks/useFetchPaymentData'
import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import payments, { Payments } from 'modules/payroll_bundle/store/collections/payments'

import EmptyListItem from './EmptyListItem'
import PaymentListItem from './PaymentListItem'
import SkeletonLoader from './SkeletonLoader'

interface Props {
  currency: string
}

const PaymentSummary = ({ currency }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { isLoading } = useFetchPaymentData()
  const { policyId, currentPeriod, currentPeriodStatus } = usePolicyPeriods()
  const { resultDispatch } = usePayrollResults()
  const periodId = currentPeriod?.id

  const periodPayments = payments.currentPeriodPayments(periodId)
  const orderedPeriodPayments = Payments.sortPayments(periodPayments)

  const lastPayment = orderedPeriodPayments?.[payments.length - 1]
  const regularPayroll = periodPayments.find((payment) => payment.concept === 'regular_payroll')
  const otherPayments = periodPayments.find((payment) => payment.concept !== 'regular_payroll')

  const resultPath = useMemo(() => {
    if (!policyId || currentPeriod?.id === undefined) {
      return navigation.payrollBundle.results.fullPath()
    }
    return navigation.payrollBundle.policy.period.offcycles.results.fullPath({
      policyId,
      periodId,
    })
  }, [policyId, periodId, currentPeriod?.id, navigation])

  if (isLoading) {
    return <SkeletonLoader />
  }

  const addOffCycle = () => {
    navigate(resultPath)
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showUploadModal: true,
      },
    })
    track(trackingEvents.payroll.userClicksOnNewPayrollPayment, {
      period_status: currentPeriodStatus,
    })
  }

  return (
    <Box gap="s8">
      <Box height="s32" flexDirection="row" justifyContent="spaceBetween" alignItems="baseline">
        <>
          <Box height="s32" flexDirection="row" alignItems="center" paddingX="s8">
            <Text.Normal weight="medium" size="300">
              {i18n.t('payroll_bundle.payroll_dashboard.payment_summary.title')}
            </Text.Normal>
            <Disclosure
              tooltipText={i18n.t(
                'payroll_bundle.payroll_dashboard.payment_summary.heading_tooltip_payments.tooltip'
              )}
            />
          </Box>
          <Button
            type="secondary"
            onClick={addOffCycle}
            iconName="add"
            aria-label={i18n.t('payroll_bundle.accessibility.new_payment')}
          >
            {i18n.t('payroll_bundle.payroll_dashboard.payment_summary.new_payment')}
          </Button>
        </>
      </Box>

      <Box
        border={{
          all: {
            width: 's1',
            color: 'grey400',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'abs012',
        }}
      >
        {!regularPayroll && <EmptyListItem hasOtherPayment={!!otherPayments} />}
        {periodPayments &&
          periodPayments.map((payment) => (
            <PaymentListItem
              key={payment.attributes.id}
              payment={payment}
              lastPaymentId={lastPayment?.id}
              hasSinglePayment={payments.length === 1}
              currency={currency}
            />
          ))}
      </Box>
    </Box>
  )
}

export default observer(PaymentSummary)
